import React from 'react';
import { Dialog, DialogContent, DialogTitle, Box, Typography, CardMedia, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useOffer } from '../../hooks/useOffers';
import { LoadingSpinner } from '@shared/components';
import { useOfferConfig } from '../../contexts';
import RenderOfferOverlay from './renderOfferOverlay';
import { getLabelFromOptionId, resolveImageUrl } from '@modules/offers/utils';
// import OfferId from './offerId';

interface OfferPreviewDialogProps {
  open: boolean;
  offerId?: number | string;
  onClose: () => void;
}

const offerType = {
  gold_mark: ['a24'],
  premium_account: ['a178'],
  hero_medals: ['a23134', 'a50639'],
};

const dymentionMapping = {
  gold_mark: { width: '181px', height: '300px' },
  premium_account: { width: '198px', height: '300px' },
  hero_medals: { width: '349px', height: '300px' },
};

const typeTextMapping = {
  gold_mark: 'Gold Mark Pack',
  premium_account: 'Premium Account Offer',
  hero_medals: 'Hero Medals Bundle',
};

const OfferPreviewDialog: React.FC<OfferPreviewDialogProps> = ({ open, offerId, onClose }) => {
  const enabledId = open && offerId ? Number(offerId) : 0;
  const { data: offer, isLoading, isError } = useOffer(enabledId || 0);
  const { premiumOptions, currencyOptions } = useOfferConfig();

  if (!offer) {
    return;
  }
  // Parse the premium ID
  const firstPremiumId = offer?.premium_ids?.split(',')[0].split('=');

  // Find option
  const selectedOption = premiumOptions.find(({ optionId }) => String(optionId) === firstPremiumId[0]);

  // Find the type from offerType using selectedOption.value
  const optionValue = selectedOption?.value;

  // Find type key in offerType containing the optionValue
  const typeKey = Object.keys(offerType).find((key) => offerType[key].includes(optionValue));

  // Get dimensions based on typeKey
  const dimensions = typeKey ? dymentionMapping[typeKey] : { width: '181px', height: '300px' };

  const price = `${(offer.price || 0).toFixed(2)} ${
    offer.currency ? getLabelFromOptionId(offer.currency, currencyOptions) : 'EUR'
  }`;

  // selectedOption.value: "a24"
  const overlayText = typeKey ? typeTextMapping[typeKey] : '';
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Offer Preview
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading && <LoadingSpinner fullHeight message="Loading preview..." />}
        {!isLoading && isError && (
          <Typography color="error" variant="body2">
            Failed to load offer for preview.
          </Typography>
        )}
        {!isLoading && !isError && offer && (
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            {/* Desktop Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Desktop
              </Typography>

              <Box
                sx={{
                  position: 'relative',
                  outline: '1px solid #fff',
                  backgroundColor: '#000',
                  boxShadow: '1px 1px 5px 1px #4c4c4c',
                  width: dimensions.width,
                  height: dimensions.height,
                }}
              >
                {typeKey && RenderOfferOverlay(typeKey, offer, firstPremiumId[1])}
                {/* <OfferId id={offer.prod_external_offer_id || offer.external_offer_id || offer.offer_id} /> */}
                <CardMedia
                  component="img"
                  image={resolveImageUrl(offer.desktop_background)}
                  alt="Desktop Background"
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxHeight: 349,
                    minWidth: 181,
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                {/* Price overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    height: '55px',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    backgroundColor: '#130e0abf',
                    borderTop: '1px solid #ffffff4d',
                  }}
                >
                  {typeKey === 'hero_medals' && (
                    <Typography variant="subtitle1" sx={{ fontSize: '12px', fontWeight: 400 }}>
                      Limit: 1
                    </Typography>
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '14px' }}>
                    {price}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Mobile Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Mobile
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  outline: '1px solid #fff',
                  backgroundColor: '#000',
                  boxShadow: '1px 1px 5px 1px #4c4c4c',
                  width: dimensions.width,
                  height: dimensions.height,
                }}
              >
                {typeKey && RenderOfferOverlay(typeKey, offer, firstPremiumId[1])}
                {/* <OfferId id={offer.prod_external_offer_id || offer.external_offer_id || offer.offer_id} /> */}
                <CardMedia
                  component="img"
                  image={resolveImageUrl(offer.mobile_background)}
                  alt="Desktop Background"
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxHeight: 349,
                    minWidth: 181,
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                {/* Price overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    height: '55px',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    backgroundColor: '#130e0abf',
                    borderTop: '1px solid #ffffff4d',
                  }}
                >
                  {typeKey === 'hero_medals' && (
                    <Typography variant="subtitle1" sx={{ fontSize: '12px', fontWeight: 400 }}>
                      Limit: 1
                    </Typography>
                  )}
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {price}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OfferPreviewDialog;
